import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LogoHeader from './LogoHeader';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(null);
    setMobileExpanded({});
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const isAnyServiceActive = () => {
    const p = location.pathname;
    return p.includes('/reparation-baie') || p.includes('/changer-roulettes') ||
      p.includes('/baie-coulissante-qui-coince') || p.includes('/reparation-rail-baie') ||
      p.includes('/reglage-baie') || p.includes('/serrure-cremone') ||
      p.includes('/remplacement-baie') || p.includes('/double-vitrage-baie') ||
      p.includes('/reparation-volet') || p.includes('/volet-roulant') ||
      p.includes('/motorisation-volet') || p.includes('/changer-sangle') ||
      p.includes('/vitrier') || p.includes('/remplacement-vitre') ||
      p.includes('/remplacement-double-vitrage') || p.includes('/double-vitrage-embue') ||
      p.includes('/miroiterie') || p.includes('/survitrage') ||
      p.includes('/reparation-store') || p.includes('/store-banne') ||
      p.includes('/motorisation-store') || p.includes('/reparation-bso') ||
      p.includes('/store-electrique') || p.includes('/reparation-fenetre') ||
      p.includes('/fenetre') || p.includes('/changer-cremone') ||
      p.includes('/changer-poignee') || p.includes('/reglage-oscillo') ||
      p.includes('/changer-joint') || p.includes('/depannage-menuiserie');
  };

  const silo1Links = [
    { to: '/reparation-baie-coulissante-herault/', label: 'Présentation' },
    { to: '/changer-roulettes-galets-baie-coulissante-herault/', label: 'Roulettes & galets' },
    { to: '/baie-coulissante-qui-coince-herault/', label: 'Baie qui coince' },
    { to: '/reparation-rail-baie-coulissante-herault/', label: 'Rail de baie' },
    { to: '/reglage-baie-coulissante-herault/', label: 'Réglage baie' },
    { to: '/serrure-cremone-baie-coulissante-herault/', label: 'Serrure & crémone' },
    { to: '/remplacement-baie-coulissante-herault/', label: 'Remplacement baie' },
    { to: '/double-vitrage-baie-coulissante-herault/', label: 'Double vitrage baie' },
  ];

  const silo2Links = [
    { to: '/reparation-volet-roulant-herault/', label: 'Présentation' },
    { to: '/volet-roulant-bloque-herault/', label: 'Volet bloqué' },
    { to: '/volet-roulant-ne-remonte-plus-herault/', label: 'Ne remonte plus' },
    { to: '/reparation-moteur-volet-roulant-herault/', label: 'Moteur' },
    { to: '/changer-sangle-volet-roulant-herault/', label: 'Sangle' },
    { to: '/motorisation-volet-roulant-herault/', label: 'Motorisation' },
    { to: '/volet-roulant-electrique-herault/', label: 'Volet électrique' },
    { to: '/volet-roulant-solaire-herault/', label: 'Volet solaire' },
  ];

  const silo3Links = [
    { to: '/vitrier-herault/', label: 'Présentation' },
    { to: '/remplacement-vitre-cassee-herault/', label: 'Vitre cassée' },
    { to: '/remplacement-double-vitrage-herault/', label: 'Double vitrage' },
    { to: '/double-vitrage-embue-herault/', label: 'Vitrage embué' },
    { to: '/vitrier-urgence-herault/', label: 'Urgence' },
    { to: '/miroiterie-herault/', label: 'Miroiterie' },
    { to: '/survitrage-herault/', label: 'Survitrage' },
  ];

  const silo4Links = [
    { to: '/reparation-store-herault/', label: 'Présentation' },
    { to: '/reparation-store-banne-herault/', label: 'Store banne' },
    { to: '/store-banne-bloque-herault/', label: 'Store banne bloqué' },
    { to: '/motorisation-store-herault/', label: 'Motorisation' },
    { to: '/reparation-bso-herault/', label: 'BSO' },
    { to: '/store-electrique-herault/', label: 'Store électrique' },
  ];

  const silo5Links = [
    { to: '/reparation-fenetre-herault/', label: 'Présentation' },
    { to: '/fenetre-bloquee-herault/', label: 'Fenêtre bloquée' },
    { to: '/changer-cremone-fenetre-herault/', label: 'Crémone' },
    { to: '/changer-poignee-fenetre-herault/', label: 'Poignée' },
    { to: '/reglage-oscillo-battant-herault/', label: 'Oscillo-battant' },
    { to: '/changer-joint-fenetre-herault/', label: 'Joint' },
    { to: '/reparation-fenetre-pvc-alu-bois-herault/', label: 'PVC, alu, bois' },
  ];

  const villesLinks = [
    { to: '/depannage-menuiserie-montpellier/', label: 'Montpellier' },
    { to: '/depannage-menuiserie-beziers/', label: 'Béziers' },
    { to: '/depannage-menuiserie-sete/', label: 'Sète' },
    { to: '/depannage-menuiserie-agde/', label: 'Agde' },
    { to: '/depannage-menuiserie-frontignan/', label: 'Frontignan' },
    { to: '/depannage-menuiserie-lattes/', label: 'Lattes' },
    { to: '/depannage-menuiserie-la-grande-motte/', label: 'La Grande-Motte' },
    { to: '/depannage-menuiserie-castelnau-le-lez/', label: 'Castelnau-le-Lez' },
    { to: '/depannage-menuiserie-mauguio/', label: 'Mauguio' },
    { to: '/depannage-menuiserie-lunel/', label: 'Lunel' },
    { to: '/depannage-menuiserie-clermont-l-herault/', label: "Clermont-l'Hérault" },
    { to: '/depannage-menuiserie-pezenas/', label: 'Pézenas' },
    { to: '/depannage-menuiserie-meze/', label: 'Mèze' },
    { to: '/depannage-menuiserie-juvignac/', label: 'Juvignac' },
    { to: '/depannage-menuiserie-saint-jean-de-vedas/', label: 'Saint-Jean-de-Védas' },
    { to: '/depannage-menuiserie-villeneuve-les-maguelone/', label: 'Villeneuve-lès-Maguelone' },
    { to: '/depannage-menuiserie-saint-gely-du-fesc/', label: 'Saint-Gély-du-Fesc' },
    { to: '/depannage-menuiserie-le-cres/', label: 'Le Crès' },
    { to: '/depannage-menuiserie-perols/', label: 'Pérols' },
    { to: '/depannage-menuiserie-grabels/', label: 'Grabels' },
    { to: '/depannage-menuiserie-pignan/', label: 'Pignan' },
    { to: '/depannage-menuiserie-marseillan/', label: 'Marseillan' },
  ];

  const toggleMobileSection = (section: string) => {
    setMobileExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const megaMenuColumns = [
    { title: 'Baie coulissante', links: silo1Links, siloUrl: '/reparation-baie-coulissante-herault/' },
    { title: 'Volet roulant', links: silo2Links, siloUrl: '/reparation-volet-roulant-herault/' },
    { title: 'Vitrage', links: silo3Links, siloUrl: '/vitrier-herault/' },
    { title: 'Store / BSO', links: silo4Links, siloUrl: '/reparation-store-herault/' },
    { title: 'Fenêtre', links: silo5Links, siloUrl: '/reparation-fenetre-herault/' },
  ];

  return (
    <>
      {/* Spacer to compensate fixed header */}
      <div className="h-16" />

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 bg-white ${
          scrolled ? 'shadow-[0_1px_6px_rgba(0,0,0,0.08)]' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0 mr-8" aria-label="DFPRO Hérault — Accueil">
              <LogoHeader />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1 xl:gap-3" ref={dropdownRef}>
              <Link
                to="/"
                className={`font-label text-sm font-semibold whitespace-nowrap px-2 py-1.5 rounded transition-colors duration-200 ${
                  isActive('/') ? 'text-accent-500' : 'text-foreground-500 hover:text-accent-500'
                }`}
              >
                Accueil
              </Link>

              {/* Mega-menu Services */}
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(dropdownOpen === 'services' ? null : 'services')}
                  className={`font-label text-sm font-semibold whitespace-nowrap px-2 py-1.5 rounded transition-colors duration-200 flex items-center gap-1 ${
                    isAnyServiceActive() ? 'text-accent-500' : 'text-foreground-500 hover:text-accent-500'
                  }`}
                >
                  Services
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen === 'services' ? 'rotate-180' : ''}`}></i>
                  </span>
                </button>
                {dropdownOpen === 'services' && (
                  <div className="absolute top-full left-0 mt-2 w-[780px] bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.10)] border border-background-200 p-5 z-50">
                    <div className="grid grid-cols-5 gap-5">
                      {megaMenuColumns.map((col) => (
                        <div key={col.title}>
                          <Link
                            to={col.siloUrl}
                            className="block font-heading font-semibold text-xs uppercase tracking-wider text-primary-500 hover:text-accent-500 transition-colors duration-150 mb-2 whitespace-nowrap"
                          >
                            {col.title}
                          </Link>
                          <ul className="space-y-1">
                            {col.links.map((link) => (
                              <li key={link.to}>
                                <Link
                                  to={link.to}
                                  className={`block text-sm font-body transition-colors duration-150 whitespace-nowrap ${
                                    isActive(link.to) ? 'text-accent-500 font-semibold' : 'text-foreground-500 hover:text-accent-500'
                                  }`}
                                >
                                  {link.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Villes */}
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(dropdownOpen === 'villes' ? null : 'villes')}
                  className={`font-label text-sm font-semibold whitespace-nowrap px-2 py-1.5 rounded transition-colors duration-200 flex items-center gap-1 ${
                    location.pathname.includes('/depannage-menuiserie') ? 'text-accent-500' : 'text-foreground-500 hover:text-accent-500'
                  }`}
                >
                  Villes
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen === 'villes' ? 'rotate-180' : ''}`}></i>
                  </span>
                </button>
                {dropdownOpen === 'villes' && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.10)] border border-background-200 py-2 z-50 max-h-80 overflow-y-auto">
                    {villesLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`block px-4 py-2 text-sm font-body transition-colors duration-150 ${
                          isActive(link.to) ? 'text-accent-500 bg-accent-100/50 font-semibold' : 'text-foreground-500 hover:bg-background-50 hover:text-accent-500'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/blog/"
                className={`font-label text-sm font-semibold whitespace-nowrap px-2 py-1.5 rounded transition-colors duration-200 ${
                  isActive('/blog/') ? 'text-accent-500' : 'text-foreground-500 hover:text-accent-500'
                }`}
              >
                Blog
              </Link>

              <Link
                to="/contact/"
                className={`font-label text-sm font-semibold whitespace-nowrap px-2 py-1.5 rounded transition-colors duration-200 ${
                  isActive('/contact/') ? 'text-accent-500' : 'text-foreground-500 hover:text-accent-500'
                }`}
              >
                Contact
              </Link>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center shrink-0 ml-3">
              <a
                href="tel:+33980802242"
                className="btn-call text-sm px-4 py-2"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm"></i>
                </span>
                09 80 80 22 42
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-primary-500 hover:text-accent-500 transition-colors duration-200"
              aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`text-xl transition-transform duration-300 ${mobileOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
              </span>
            </button>
          </div>
        </div>

      {/* Mobile overlay + slide-in panel */}
      <>
        {/* Backdrop overlay */}
        <div
          onClick={() => setMobileOpen(false)}
          className={`lg:hidden fixed inset-0 top-16 bg-black/40 z-40 transition-opacity duration-300 ${
            mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        />

        {/* Slide-in panel */}
        <div
          className={`lg:hidden fixed top-16 right-0 h-[calc(100vh-4rem)] w-[300px] max-w-[85vw] bg-white z-50 transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-[-4px_0_20px_rgba(0,0,0,0.08)] overflow-y-auto ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <nav className="flex flex-col py-6 px-5 gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className={`font-label text-sm font-semibold py-3 px-3 rounded-lg transition-colors duration-150 ${
                isActive('/') ? 'bg-accent-50 text-accent-500' : 'text-foreground-500 hover:bg-background-50'
              }`}
            >
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-home-4-line text-base"></i></span>
                Accueil
              </span>
            </Link>

            {/* Services repliable */}
            <div>
              <button
                onClick={() => toggleMobileSection('services')}
                className={`w-full flex items-center justify-between font-label text-sm font-semibold py-3 px-3 rounded-lg transition-colors duration-150 ${
                  isAnyServiceActive() ? 'bg-accent-50 text-accent-500' : 'text-foreground-500 hover:bg-background-50'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-tools-line text-base"></i></span>
                  Services
                </span>
                <span className="w-4 h-4 flex items-center justify-center transition-transform duration-200">
                  <i className={`ri-arrow-down-s-line text-sm ${mobileExpanded['services'] ? 'rotate-180' : ''}`}></i>
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                mobileExpanded['services'] ? 'max-h-[600px] opacity-100 mt-1' : 'max-h-0 opacity-0'
              }`}>
                <div className="ml-4 pl-4 border-l-2 border-background-200 space-y-4 py-2">
                  {megaMenuColumns.map((col) => (
                    <div key={col.title}>
                      <Link
                        to={col.siloUrl}
                        onClick={() => setMobileOpen(false)}
                        className="block font-heading font-semibold text-xs text-primary-500 uppercase tracking-wider py-1"
                      >
                        {col.title}
                      </Link>
                      <div className="mt-1 space-y-0.5">
                        {col.links.map((link) => (
                          <Link
                            key={link.to}
                            to={link.to}
                            onClick={() => setMobileOpen(false)}
                            className={`block font-body text-sm py-1.5 pl-2 rounded transition-colors duration-150 ${
                              isActive(link.to) ? 'text-accent-500 font-semibold bg-accent-50' : 'text-foreground-500 hover:text-accent-500'
                            }`}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Villes repliable */}
            <div>
              <button
                onClick={() => toggleMobileSection('villes')}
                className={`w-full flex items-center justify-between font-label text-sm font-semibold py-3 px-3 rounded-lg transition-colors duration-150 ${
                  location.pathname.includes('/depannage-menuiserie') ? 'bg-accent-50 text-accent-500' : 'text-foreground-500 hover:bg-background-50'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-map-pin-line text-base"></i></span>
                  Villes
                </span>
                <span className="w-4 h-4 flex items-center justify-center transition-transform duration-200">
                  <i className={`ri-arrow-down-s-line text-sm ${mobileExpanded['villes'] ? 'rotate-180' : ''}`}></i>
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                mobileExpanded['villes'] ? 'max-h-[500px] opacity-100 mt-1' : 'max-h-0 opacity-0'
              }`}>
                <div className="ml-4 pl-4 border-l-2 border-background-200 py-2 space-y-0.5">
                  {villesLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className={`block font-body text-sm py-1.5 pl-2 rounded transition-colors duration-150 ${
                        isActive(link.to) ? 'text-accent-500 font-semibold bg-accent-50' : 'text-foreground-500 hover:text-accent-500'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/blog/"
              onClick={() => setMobileOpen(false)}
              className={`font-label text-sm font-semibold py-3 px-3 rounded-lg transition-colors duration-150 ${
                isActive('/blog/') ? 'bg-accent-50 text-accent-500' : 'text-foreground-500 hover:bg-background-50'
              }`}
            >
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-article-line text-base"></i></span>
                Blog
              </span>
            </Link>

            <Link
              to="/contact/"
              onClick={() => setMobileOpen(false)}
              className={`font-label text-sm font-semibold py-3 px-3 rounded-lg transition-colors duration-150 ${
                isActive('/contact/') ? 'bg-accent-50 text-accent-500' : 'text-foreground-500 hover:bg-background-50'
              }`}
            >
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line text-base"></i></span>
                Contact
              </span>
            </Link>

            {/* Divider */}
            <div className="my-4 border-t border-background-200" />

            {/* CTA mobile */}
            <div className="px-3">
              <a
                href="tel:+33980802242"
                onClick={() => setMobileOpen(false)}
                className="btn-call text-sm w-full justify-center py-3"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm"></i>
                </span>
                09 80 80 22 42
              </a>
              <p className="text-xs text-center text-foreground-500/50 mt-3 font-body">
                Devis gratuit &middot; 7j/7 &middot; Garantie 10 ans
              </p>
            </div>
          </nav>
        </div>
      </>
      </header>
    </>
  );
}